import React from 'react';
import { ArrowRight, CheckCircle, Lock } from 'lucide-react';

interface OtpVerificationStepProps {
  email: string;
  code: string;
  onCodeChange: (code: string) => void;
  onSubmit: () => void;
  onResend: () => void;
  loading?: boolean;
  title?: string;
  /** Shows a "Cambiar correo" link next to the resend action. */
  onChangeEmail?: () => void;
  children?: React.ReactNode;
}

const OtpVerificationStep: React.FC<OtpVerificationStepProps> = ({
  email,
  code,
  onCodeChange,
  onSubmit,
  onResend,
  loading = false,
  title = 'Verifica tu email',
  onChangeEmail,
  children,
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-[#E8F7F7] rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-[#1CB3B3]" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">{title}</h2>
        <p className="text-gray-600 text-sm">
          Hemos enviado un código de verificación a <br />
          <span className="font-medium text-gray-900">{email}</span>
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Código de verificación *
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Lock className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            id="otp-input"
            value={code}
            onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            maxLength={6}
            inputMode="numeric"
            autoComplete="one-time-code"
            className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#1CB3B3] focus:bg-[#E8F7F7] transition-all duration-200 text-center text-2xl tracking-widest font-mono"
            disabled={loading}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Ingresa el código de 6 dígitos que recibiste por email
        </p>
      </div>

      <button
        type="submit"
        id="otp-verify-btn"
        disabled={code.length !== 6 || loading}
        className="w-full bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white py-3 px-6 rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 text-base shadow-lg hover:shadow-xl"
      >
        {loading ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>Verificando...</span>
          </>
        ) : (
          <>
            <span>Verificar código</span>
            <ArrowRight className="h-5 w-5" />
          </>
        )}
      </button>

      <div className="text-center space-y-2">
        <button
          type="button"
          onClick={onResend}
          disabled={loading}
          className="text-sm text-[#1CB3B3] hover:text-[#0D7377] font-medium"
        >
          ¿No recibiste el código? Reenviar
        </button>
        {onChangeEmail && (
          <p className="text-sm text-gray-600">
            ¿Escribiste mal tu correo?{' '}
            <button
              type="button"
              onClick={onChangeEmail}
              disabled={loading}
              className="text-[#1CB3B3] hover:text-[#0D7377] font-medium"
            >
              Cambiar correo
            </button>
          </p>
        )}
        {children}
      </div>
    </form>
  );
};

export default OtpVerificationStep;
